import { useAppSelector } from "@/app/hooks";
import { useGetClientLogoQuery } from "@/services/mediaService";
import { useGetTourStopsQuery } from "@/services/survey";
import { formatCoverDate } from "@/shared/utils/formatCoverDate";
import { useState } from "react";
import { useParams } from "react-router";
import InteractiveSurveyContactPreviewList from "./components/InteractiveSurveyContactPreviewList";

// Define interface for contact data structure
interface Contact {
  contactId: string;
  name: string;
  title?: string;
  licenseNo?: string;
  email: string;
  phoneNumber?: string;
  order: number;
}

// Define interface for cover background image
interface CoverImage {
  cloudfrontUrl: string;
}

/**
 * Interactive Survey Cover Page
 * Main entry point for authenticated survey users
 */
const InteractiveSurveyCover = () => {
  const { surveyId } = useParams<{ surveyId: string }>();
  const { currentSurvey } = useAppSelector((state) => state.interactiveSurvey);

  // Get client logo if display is enabled
  const { data: clientLogoData } = useGetClientLogoQuery(surveyId || "", {
    skip: !surveyId || !currentSurvey?.isDisplayClientLogo, // Skip if no surveyId or if client logo display is disabled
  });

  // Prefetch tour stops data for tour schedule page
  useGetTourStopsQuery(surveyId || "", {
    skip: !surveyId,
  });

  // State for handling logo errors
  const [logoImgError, setLogoImgError] = useState(false);
  const [imageError, setImageError] = useState(false);

  // Default cover image URL - warehouse3 image as fallback
  const defaultCoverImage = "/warehouse3.jpg";

  const survey = currentSurvey;

  // Early return if no survey data
  if (!survey || Object.keys(survey).length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col h-screen lg:flex-row relative bg-cbre-green overflow-y-auto">
      {/* Left Panel */}
      <div className="w-full lg:w-1/2 relative text-white bg-cbre-green lg:min-h-full order-1 lg:order-1 flex flex-col lg:overflow-y-auto">
        {/* Content Container with responsive spacing and no scrolling */}
        <div
          className="flex-1 flex flex-col justify-center relative px-4 sm:px-6 md:px-8 lg:px-12"
          style={{ paddingTop: "clamp(0, 12vw, 200px)" }}
        >
          <main
            className="lg:pr-20 max-w-2xl w-full mb-5 lg:mb-[50%] mt-[50px]"
            aria-label="Survey information"
          >
            {/* Inner container with responsive spacing and accessibility */}
            {/* Title Section with responsive typography */}
            <div>
              <h1
                className="text-white mb-2 fancy"
                style={{
                  fontSize: "clamp(32px, 4vw, 72px)",
                  fontWeight: 400,
                  lineHeight: "clamp(28px, 4.5vw, 72px)",
                  letterSpacing: "0.5px",
                  wordBreak: "break-word",
                  overflowWrap: "break-word",
                  whiteSpace: "normal",
                  marginBottom: "24px",
                }}
              >
                {survey?.coverTitle || "Property Survey"}
              </h1>
            </div>
            {/* Horizontal Divider with responsive sizing */}
            <div
              className="bg-white mb-4 sm:mb-6"
              style={{
                height: "clamp(2px, 0.5vw, 3px)", // Responsive height
                width: "calc(100% + 5rem)",
                margin: "0",
                marginBottom: "clamp(16px, 3vw, 24px)",
                borderRadius: "2px",
                position: "relative",
                zIndex: 2,
              }}
            />
            {/* Cover Subtitle with responsive typography */}
            {survey?.coverSubTitle && (
              <div className="mb-4 sm:mb-6 z-10 wrap-break-word overflow-hidden">
                <p
                  className="text-white"
                  style={{
                    fontFamily:
                      '"Calibre", "Helvetica Neue", Arial, sans-serif',
                    fontSize: "20px", // Responsive font size
                    fontWeight: 500,
                    lineHeight: "120%", // Responsive line height
                    letterSpacing: "0.3px",
                  }}
                >
                  {survey.coverSubTitle}
                </p>
              </div>
            )}
            {/* Date and Client Name Section with responsive layout */}
            <div className="mb-6 sm:mb-8">
              <div className="flex flex-col text-white gap-1">
                {/* Client Name (if enabled and available) */}
                {survey?.isDisplayClientName && survey?.clientName && (
                  <span
                    style={{
                      fontFamily:
                        '"Calibre", "Helvetica Neue", Arial, sans-serif',
                      fontSize: "16px",
                      fontWeight: 500,
                      opacity: 0.9,
                      width: "100%",
                      overflowWrap: "break-word",
                      wordBreak: "break-word",
                    }}
                  >
                    {survey.clientName}
                  </span>
                )}
                {/* Date */}
                <span className="updated-date">
                  {formatCoverDate(survey?.coverDate, survey?.updatedOn)}
                </span>
              </div>
            </div>
            {/* Client Logo Section with responsive sizing */}
            {survey?.isDisplayClientLogo &&
              clientLogoData &&
              clientLogoData.length > 0 &&
              !logoImgError && (
                <div
                  className="mt-4 sm:mt-6 lg:mt-8"
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                  }}
                >
                  <img
                    src={clientLogoData[0].cloudfrontUrl}
                    alt="Client Logo"
                    style={{
                      objectFit: "contain",
                      height: "auto",
                      width: "auto",
                      maxWidth: survey?.unrestrictedLogoSize
                        ? "clamp(200px, 60vw, 500px)" // Responsive unrestricted size
                        : "125px", // Standard width
                      maxHeight: survey?.unrestrictedLogoSize
                        ? "clamp(150px, 60vw, 500px)" // Responsive unrestricted height
                        : "125px", // Standard height
                      backgroundColor: survey?.isClientLogoTransparent
                        ? "transparent"
                        : "rgba(255,255,255,0.95)",
                      padding: survey?.isClientLogoTransparent ? "0" : "4px",
                      boxShadow: survey?.isClientLogoTransparent
                        ? "none"
                        : "0 2px 8px rgba(0,0,0,0.1)",
                    }}
                    onError={() => setLogoImgError(true)}
                  />
                </div>
              )}
          </main>
        </div>
      </div>

      {/* Right Panel - Cover Image with responsive sizing, ordering and safe areas */}
      <div className="right-panel w-full lg:w-1/2 relative order-2 lg:order-2 flex-1 min-h-[40vh] sm:min-h-[50vh] lg:min-h-full">
        {/* absolute (not h-full) — percentage height doesn't reliably resolve against a flex-grown parent here */}
        <div className="absolute inset-0">
          {/* Image container with loading state and responsive object positioning */}
          <img
            src={(() => {
              if (!imageError && survey?.coverBackgroundImage) {
                if (typeof survey.coverBackgroundImage === "string") {
                  return survey.coverBackgroundImage;
                } else {
                  const cloudfrontUrl = (
                    survey.coverBackgroundImage as CoverImage
                  ).cloudfrontUrl;
                  // Check if cloudfrontUrl is a valid non-empty string
                  if (cloudfrontUrl && cloudfrontUrl.trim() !== "") {
                    return cloudfrontUrl + "?d=web";
                  } else {
                    // Use fallback URL if cloudfrontUrl is empty
                    return `${import.meta.env.VITE_MEDIA_BASE_URL}/cover_photos/9333eb85-46fc-44b0-a275-a729d8ae0b8c`;
                  }
                }
              }
              return defaultCoverImage;
            })()}
            alt={
              survey?.coverBackgroundImage && !imageError
                ? "Survey Cover"
                : "Warehouse Placeholder"
            }
            className="h-full w-full object-cover transition-opacity duration-300"
            style={{
              // Better object positioning for different aspect ratios
              objectPosition: "center center",
            }}
            onLoad={() => {
              // Image loaded successfully
            }}
            onError={() => {
              // If the survey image fails to load, fallback to default image
              if (!imageError) {
                setImageError(true);
              }
            }}
          />

          {/* Overlay gradient for better text contrast on mobile when needed */}
          <div
            className="absolute inset-0 bg-linear-to-t from-black/20 to-transparent lg:hidden pointer-events-none"
            style={{ zIndex: 1 }}
          />
        </div>
        {/* Contact Cards */}
        <div className="contact-area">
          {survey?.contacts && survey.contacts.length > 0 && (
            <InteractiveSurveyContactPreviewList
              surveyContactList={survey.contacts as Contact[]}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default InteractiveSurveyCover;
